import React, { createContext, useCallback, useState, useEffect, useRef, useContext } from "react";
import { View, Text, Linking, TextInput, ScrollView, ImageBackground, Modal, Animated, Button, Pressable, Image, Switch, Picker, Dimensions, TouchableOpacity, ActivityIndicator } from "react-native";
import { getAuth, signInAnonymously, createUserWithEmailAndPassword, signInWithEmailAndPassword, onAuthStateChanged, signOut } from "firebase/auth";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

import { app } from "../firebase";
import { getFirestore, doc, getDoc, setDoc, query, where, getDocs, collection } from "firebase/firestore";

import { useUser } from "../Widgets/UserContext";
import { useNavigation, useFocusEffect } from "@react-navigation/native";
import { usePageTracking } from '../Hooks/usePageTracking';
import useWindowDimensions from "../Hooks/useWindowDimensions";
import styles from "../Styles/RegistryStyles";
import HeaderBar, { useScrollHide } from "../Widgets/HeaderBar";
import RegistrySetupFlow from "./RegistryWidgets/RegistrySetup/RegistrySetupFlow";
import Yearbook from "./RegistryWidgets/Yearbook";
import GoalTracker from "./RegistryWidgets/GoalTracker";
import Share from "./Share";
import { BackgroundProvider } from './Helpers/BackgroundContext';
import PrivatePageMessage from "./Helpers/PrivatePageMessage"; // Import the new component
import PageUnavailableMessage from "./Helpers/PageUnavailableMessage";
import { HEADER_PADDING } from "../constants";
import { MYTASSEL_PLUS_FEATURES } from "../constants/MyTasselPlus";
import useMyTasselPlus from "../Hooks/useMyTasselPlus";
import {
	extractMyTasselRouteSegment,
	extractMyTasselVanitySlug,
	getMyTasselPublicUrls,
	resolveMyTasselRouteUid,
} from "../services/VanitySlugService";
import { TutorialPopup, useTutorial } from "./Helpers/Tutorials";
import { subscribeToSpotlightEvents, useEmitSpotlightEventWhenOpened } from "../Widgets/SpotlightTutorial/spotlightBus";
import { SPOTLIGHT_EVENTS } from "../Widgets/SpotlightTutorial/spotlightEventNames";
import { SPOTLIGHT_TARGETS, spotlightTarget, spotlightTargetWhen } from "../Widgets/SpotlightTutorial/spotlightTargets";
// import { buttonHoverStyle } from '../../utils/hoverStyles'; // Add this import at the top
import SEOHead from '../components/SEOHead';
import VisitorViewBanner from './Components/VisitorViewBanner';
import { ProductStructuredData } from '../components/StructuredData';
import AnalyticsManager from '../services/AnalyticsManager';

let LOCAL_SERVER_URL = "http://localhost:5001/my-tassel/us-central1/api";
let FIREBASE_SERVER_URL = "https://us-central1-my-tassel.cloudfunctions.net/api";
// Always use production Firebase Functions (no emulators)
const SERVER_URL = FIREBASE_SERVER_URL;

// Helper to get base URL based on current environment
const getBaseUrl = () => {
	if (typeof window === 'undefined') return 'https://mytassel.com';
	const hostname = window.location?.hostname;
	if (hostname === 'mytassel.com' || hostname === 'www.mytassel.com') {
		return 'https://mytassel.com';
	}
	// For localhost, staging, or any other environment, use current origin
	return window.location?.origin || 'https://mytassel.com';
};

const Registry = ({ route }) => {
	const {
		setShowTutorial,
		setActivePopupId,
		showTutorial,
		isLoading,
		tutorialsToShow,
		setClosedTutorials,
		setTutorialsToShow,
		activePopupId,
	} = useTutorial();

	//#region - Variables
	// Initialize Firestore
	const db = getFirestore(app);
	const { headerTranslateY, onScroll } = useScrollHide('scrollView');
	const { currentUser, currentUserData, authLoading } = useUser();
	const myTasselPlus = useMyTasselPlus();
	const [pageOwner, setPageOwner] = useState(null);
	const [isPageOwnerReady, setIsPageOwnerReady] = useState(false);
	const [pageNotFound, setPageNotFound] = useState(false);
	const [pageNotFoundReason, setPageNotFoundReason] = useState('not_found');

	// Track page view with specific user registry
	const { uid, openCatalogue } = route.params || {};
	const [ownerDisplayName, setOwnerDisplayName] = useState('Unknown User');
	
	// Get owner's display name for tracking
	useEffect(() => {
		const getOwnerDisplayName = async () => {
			if (uid && pageOwner) {
				const displayName = pageOwner.firstName && pageOwner.lastName 
					? `${pageOwner.firstName} ${pageOwner.lastName}`
					: pageOwner.email || pageOwner.displayName || `User ${uid.substring(0, 8)}`;
				setOwnerDisplayName(displayName);
			} else if (uid) {
				// Fallback: try to get user data if pageOwner isn't available yet
				try {
					const userDoc = await getDoc(doc(db, 'users', uid));
					if (userDoc.exists()) {
						const userData = userDoc.data();
						const displayName = userData.firstName && userData.lastName 
							? `${userData.firstName} ${userData.lastName}`
							: userData.email || userData.displayName || `User ${uid.substring(0, 8)}`;
						setOwnerDisplayName(displayName);
					} else {
						setOwnerDisplayName(`User ${uid.substring(0, 8)}`);
					}
				} catch (error) {
					setOwnerDisplayName(`User ${uid.substring(0, 8)}`);
				}
			}
		};
		
		getOwnerDisplayName();
	}, [uid, pageOwner]);
	
	usePageTracking(`Registry-${uid}`, { 
		userId: currentUser?.uid || 'anonymous',
		registryOwner: uid,
		ownerDisplayName: `User ${uid}`, // Always use User ID for privacy
		viewerType: currentUser?.uid === uid ? 'owner' : 'visitor',
		pageContext: `Registry of User ${uid}`,
		specificPage: `Registry-${uid}`
	});
	const [showCatalogue, setShowCatalogue] = useState(false);
	const [registryItems, setRegistryItems] = useState([]);
	const [isAuthorized, setIsAuthorized] = useState(false);

	const navigation = useNavigation();

	// Since users can visit without being logged in, ensure currentUser is defined
	const auth = getAuth(app);

	const [showLogoutModal, setShowLogoutModal] = React.useState(false);
	const [isOwner, setIsOwner] = useState(false);
	const [isVisitorView, setIsVisitorView] = useState(false);

	// ?preview=owner enables owner-mode UI only for verified admins.
	const isAdminOwnerPreview = (() => {
		try {
			const params = new URLSearchParams(window.location.search);
			return params.get('preview') === 'owner' && currentUserData?.accountType === 'admin';
		} catch { return false; }
	})();

	const isPreviewingAsOwner = isAdminOwnerPreview && !isVisitorView;
	const [previewNotice, setPreviewNotice] = useState('');
	const showPreviewNotice = (message = "Preview updated. Changes are not saved.") => {
		setPreviewNotice(message);
		setTimeout(() => setPreviewNotice(''), 2500);
	};

	const [showShippingAddressPopup, setShowShippingAddressPopup] = useState(false);
	const [shippingAddress, setShippingAddress] = useState("");

	// Real-time responsive state
	const windowDimensions = useWindowDimensions();
	const { dimensions, isMobile, isTablet, isSmallDesktop, isLargeDesktop } = windowDimensions || {
		dimensions: { width: 1200, height: 800 },
		isMobile: false,
		isTablet: false,
		isSmallDesktop: true,
		isLargeDesktop: false
	};
	//#endregion

	//#region - Testing Only Development
	const { setCurrentUser, setCurrentUserData } = useUser();

	useEffect(() => {
		const unsubscribe = onAuthStateChanged(auth, (authUser) => {
			if (authUser) {
				// Update the user in the context
				setCurrentUser(authUser);
				// Optionally update user data from Firestore
			} else {
				setCurrentUser(null);
				setCurrentUserData(null);
			}
		});

		return () => unsubscribe(); // Cleanup subscription
	}, []);

	useEffect(() => {
		// Development test login function (disabled in production)
		const testLogin = async () => {
			try {
				const testEmails = [
					"Tjhawk27@gmail.com", // Trevor
					"trevorhawkinsofficial@gmail.com", // Olivia
					"trevorhawkins@makenoisemedia.io", //Sam
					"info@dsignable.com" // Tristan
				];
				const password = "Newtjhawk27";
				const lowerCaseEmail = testEmails[2].toLowerCase(); // Default to Jennifer

				const userCredential = await signInWithEmailAndPassword(auth, lowerCaseEmail, password);
				const user = userCredential.user;

				const userDocRef = doc(db, "users", lowerCaseEmail);
				const userDocSnap = await getDoc(userDocRef);

				if (userDocSnap.exists()) {
					const userData = userDocSnap.data();
					setCurrentUser(user); // Set the auth user
					setCurrentUserData(userData); // Set the Firestore user data
				}
			} catch (error) {
				// Silent fail for test login
			}
		};
		// testLogin(); // Disabled for production
	}, []);
	//#endregion

	//#region - Page Creation Variables
	const [editMode, setEditMode] = useState(false);
	const [editableMode, setEditableMode] = useState(false);
	const [widgets, setWidgets] = useState([]);
	const [pageOwnerId, setPageOwnerId] = useState(null);
	const [showAddItemModal, setShowAddItemModal] = useState(false);
	const [itemUrl, setItemUrl] = useState("");
	const [showItemModal, setShowItemModal] = useState(false);
	const [selectedItem, setSelectedItem] = useState(null);
	const [showCatalogueModal, setShowCatalogueModal] = useState(false);
	const [catalogueItems, setCatalogueItems] = useState([]);
	useEmitSpotlightEventWhenOpened(showCatalogueModal, SPOTLIGHT_EVENTS.REGISTRY_SETUP_OPENED);
	useEffect(
		() => subscribeToSpotlightEvents(({ name }) => name === SPOTLIGHT_EVENTS.REGISTRY_SETUP_CLOSE_REQUESTED && setShowCatalogueModal(false)),
		[]
	);
	
	// Auto-open catalogue if navigated with openCatalogue param
	useEffect(() => {
		if (openCatalogue && isAuthorized) {
			setShowCatalogueModal(true);
		}
	}, [openCatalogue, isAuthorized]);

	// Prevent body scroll when modal is open, and hide the navbar so it doesn't overlap
	useEffect(() => {
		if (showCatalogueModal) {
			document.body.style.overflow = 'hidden';
			Animated.timing(headerTranslateY, {
				toValue: -100,
				duration: 250,
				useNativeDriver: true,
			}).start();
		} else {
			document.body.style.overflow = 'auto';
			Animated.timing(headerTranslateY, {
				toValue: 0,
				duration: 250,
				useNativeDriver: true,
			}).start();
		}
		return () => {
			document.body.style.overflow = 'auto';
		};
	}, [showCatalogueModal]);
	const [newItem, setNewItem] = useState({ name: "", description: "", price: "", image: null, url: "" });

	const [newItems, setNewItems] = useState([]);
	const sortedItems = [...newItems].sort((a, b) => {
		return Number(a.purchased) - Number(b.purchased) || a.name.localeCompare(b.name);
	});

	const [gridRows, setGridRows] = useState(1);
	const [selectedWidget, setSelectedWidget] = useState(null);
	const [showSavePopup, setShowSavePopup] = useState(false);
	const savePopupAnim = useRef(new Animated.Value(0)).current;
	const [showErrorPopup, setShowErrorPopup] = useState(false);
	const errorPopupAnim = useRef(new Animated.Value(0)).current;
	const [saveError, setSaveError] = useState("");

	const [showBackgroundModal, setShowBackgroundModal] = useState(false);
	const [backgroundImage, setBackgroundImage] = useState(null);
	const backgroundFileInputRef = useRef(null);
	const [applyBlur, setApplyBlur] = useState(false);
	const [textColor, setTextColor] = useState("#000000");
	const predefinedBackgrounds = [require("../assets/Popup Background.png"), require("../assets/Landing Background.png"), require("../assets/Hub Background.png")];
	const [selectedColor, setSelectedColor] = useState("#000000");
	const [showPurchaseConfirmation, setShowPurchaseConfirmation] = useState(false);
	const [selectedItemForPurchase, setSelectedItemForPurchase] = useState(null);
	const [isProcessing, setIsProcessing] = useState(false);

	const [showDidYouBuyPopup, setShowDidYouBuyPopup] = useState(false);

	// Add a new state to manage the visibility of the confirmation container
	const [showConfirmationContainer, setShowConfirmationContainer] = useState(false);

	const [hoveredButton, setHoveredButton] = useState(null);

	// Add these state variables near the other state declarations
	const [hasChanges, setHasChanges] = useState(false);
	
	// Purchase recording states
	const [showPurchaseRecordModal, setShowPurchaseRecordModal] = useState(false);
	const [showAnonymousConfirmModal, setShowAnonymousConfirmModal] = useState(false);
	const [showPurchaserInfoModal, setShowPurchaserInfoModal] = useState(false);
	const [purchaserInfo, setPurchaserInfo] = useState({ name: "", email: "", phone: "" });
	const [sessionPurchaserInfo, setSessionPurchaserInfo] = useState(null);
	const [purchaseRecordError, setPurchaseRecordError] = useState("");

	// Height synchronization for desktop (bidirectional: both match the taller of the two)
	const [goalTrackerHeight, setGoalTrackerHeight] = useState(null);
	const [yearbookHeight, setYearbookHeight] = useState(null);

	const syncedHeight = (goalTrackerHeight || yearbookHeight)
		? Math.max(goalTrackerHeight || 0, yearbookHeight || 0)
		: null;

	const handleGoalTrackerHeightChange = (height) => {
		const { width } = Dimensions.get('window');
		if (width >= 1024) setGoalTrackerHeight(height);
	};

	const handleYearbookHeightChange = (height) => {
		const { width } = Dimensions.get('window');
		if (width >= 1024) setYearbookHeight(height);
	};
	//#endregion

	//#region - Page Creation Functions
	const renderItem = (item, index) => {
		const itemStyle = item.purchased ? styles.purchasedItem : styles.itemCard;

		return (
			<Pressable
				key={item.id || index}
				onPress={() => {
					if (!item.purchased) {
						redirectToUrl(item);
						handleShowShippingAddress();
					}
				}}
				style={[
					itemStyle,
					isMobile && {
						width: '45%',
						maxWidth: 200,
						marginHorizontal: 6
					}
				]}>
				{item.purchased && <Text style={styles.purchasedLabel}>Purchased</Text>}
				{item.image && <Image source={{ uri: item.image }} style={styles.itemImage} />}
				<Text
					style={styles.itemName}
					numberOfLines={2}
					ellipsizeMode="tail"
					adjustsFontSizeToFit={false}
				>
					{item.name || ""}
				</Text>
				{item.price ? (
					<Text
						style={styles.itemPrice}
						numberOfLines={1}
						ellipsizeMode="tail"
						adjustsFontSizeToFit={false}
					>
						{item.price}
					</Text>
				) : null}
				{!item.purchased && (
					<Pressable
						style={styles.itemBuyButton}
						onPress={() => {
							redirectToUrl(item);
							handleShowShippingAddress();
						}}
					>
						<Text style={styles.itemBuyButtonText}>View Item ↗</Text>
					</Pressable>
				)}
				{renderDeleteButton(item)}
			</Pressable>
		);
	};

	const deleteItem = async (itemToDelete) => {
		if (isAdminOwnerPreview) {
			const updatedItems = [...newItems];
			const indexToRemove = updatedItems.findIndex((item) => {
				if (item.id && itemToDelete.id) return item.id === itemToDelete.id;
				return item.name === itemToDelete.name && item.url === itemToDelete.url;
			});
			if (indexToRemove !== -1) updatedItems.splice(indexToRemove, 1);
			setNewItems(updatedItems);
			showPreviewNotice("Item removed in preview. Changes are not saved.");
			return;
		}
		if (currentUserData?.uid !== pageOwner?.uid) {
			alert("You are not authorized to delete this item.");
			return;
		}

		const storage = getStorage(app);

		if (itemToDelete.image && !itemToDelete.image.includes("amazon.com/images")) {
			const imageRef = ref(storage, itemToDelete.image);
			try {
				await deleteObject(imageRef);
			} catch (error) {
				// console.warn(`Error deleting image ${itemToDelete.image}:`, error);
			}
		}

		// Remove only the first matching instance to avoid deleting duplicates accidentally
		const updatedItems = [...newItems];
		const indexToRemove = updatedItems.findIndex((item) => {
			if (item.id && itemToDelete.id) {
				return item.id === itemToDelete.id;
			}
			// Fallback: compare by name and url if no ID
			return item.name === itemToDelete.name && item.url === itemToDelete.url;
		});
		if (indexToRemove !== -1) {
			updatedItems.splice(indexToRemove, 1);
		}
		setNewItems(updatedItems);

		savePageConfig(updatedItems);
	};

	const toggleAddItemModal = () => setShowAddItemModal(!showAddItemModal);

	const toggleCatalogueModal = () => setShowCatalogueModal(!showCatalogueModal);

	const handleAddNewItem = async () => {
		if (isAdminOwnerPreview) {
			showPreviewNotice("Cannot add items in preview mode.");
			return;
		}
		if (!newItem.name || !newItem.description || !newItem.price || !newItem.image) {
			alert("Please fill out all fields and select an image before adding.");
			return;
		}

		try {
			const storageRef = ref(getStorage(app), `catalogueImages/${new Date().getTime()}_${newItem.name}`);
			const snapshot = await uploadBytes(storageRef, newItem.image);
			const imageUrl = await getDownloadURL(snapshot.ref);

			const itemToSave = {
				...newItem,
				image: imageUrl,
				url: newItem.url,
			};
			const newItemsRef = doc(collection(db, "catalogueItems"));
			await setDoc(newItemsRef, itemToSave);

			// Add the Firestore document ID to the item
			const itemWithId = {
				...itemToSave,
				id: newItemsRef.id,
				addedAt: new Date().toISOString(), // Add timestamp for analytics
			};

			const updatedItems = [...newItems, itemWithId];
			setNewItems(updatedItems);
			await savePageConfig(updatedItems);

			// Track the manually added item for analytics
			try {
				await AnalyticsManager.trackRegistryItem('registry_item_added', {
					id: itemWithId.id,
					name: itemWithId.name,
					category: itemWithId.category || 'Custom',
					subCategory: itemWithId.subCategory || 'User Added',
					price: itemWithId.price,
					url: itemWithId.url,
					image: itemWithId.image
				}, currentUser?.uid);
			} catch (trackingError) {
				// Don't block the main process if tracking fails
			}

			setNewItem({ name: "", description: "", price: "", image: null });
			setShowAddItemModal(false);
		} catch (error) {
			setShowSavePopup(false);
			setSaveError("Failed to add new item. Please try again.");
			setShowErrorPopup(true);
			Animated.timing(errorPopupAnim, {
				toValue: 1,
				duration: 500,
				useNativeDriver: true,
			}).start(() => {
				setTimeout(() => {
					Animated.timing(errorPopupAnim, {
						toValue: 0,
						duration: 500,
						useNativeDriver: true,
					}).start(() => setShowErrorPopup(false));
				}, 2000);
			});
		}
	};

	const loadCatalogueItems = async () => {
	};

	const [isAddingItems, setIsAddingItems] = useState(false);
	const [selectedItems, setSelectedItems] = useState([]);

	// Resolves to true once the items are on the Registry, false if they could not be added.
	const addItemsToRegistry = async (items) => {
		if (isAdminOwnerPreview) {
			showPreviewNotice("Cannot add catalogue items in preview mode.");
			setShowCatalogueModal(false);
			return false;
		}
		// If an add operation is already running, ignore subsequent calls
		if (isAddingItems) return false;
		setIsAddingItems(true);
		const userEmail = currentUser.email.toLowerCase();
		const userDocRef = doc(db, "users", userEmail);

		try {
			const docSnap = await getDoc(userDocRef);
			let currentRegistryPageConfig = {};
			if (docSnap.exists()) {
				currentRegistryPageConfig = docSnap.data().pages?.registryPageConfig || {};
			}

// determine duplicates against existing registry entries
		const existingArray = Array.isArray(currentRegistryPageConfig.items)
			? currentRegistryPageConfig.items
			: [];
		const existingKeys = new Set(existingArray.map(i => i.id || i.url));
		const filteredItems = items.filter(item => !existingKeys.has(item.id || item.url));

		// if some (or all) items are duplicates, ask the user whether they still want them
		const duplicateCount = items.length - filteredItems.length;
		let itemsToProcess = filteredItems;
		if (duplicateCount > 0) {
			const addAnyway = window.confirm(
				`You have already added ${duplicateCount} item${duplicateCount > 1 ? 's' : ''} to your registry.\n` +
				`Would you like to add ${duplicateCount > 1 ? 'them' : 'it'} again?`
			);
			if (addAnyway) {
				itemsToProcess = items; // include duplicates
			} else if (itemsToProcess.length === 0) {
				// user opted to skip and nothing new to add
				setIsAddingItems(false);
				return true;
			}
		}
		// Convert keywords Set to array
	const itemsToAdd = itemsToProcess.map(item => ({
		...item,
		keywords: Array.isArray(item.keywords) ? item.keywords : Array.from(item.keywords || []), // Convert Set to array
		addedAt: new Date().toISOString(), // Add timestamp for analytics
	}));

	const updatedItems = [
		...existingArray,
		...itemsToAdd
	];

		const updatedRegistryPageConfig = { ...currentRegistryPageConfig, items: updatedItems };
		// console.log("Updated Registry Page Config:", updatedRegistryPageConfig); // Log the data structure

		await setDoc(userDocRef, { pages: { registryPageConfig: updatedRegistryPageConfig } }, { merge: true });
			// console.log("Registry items updated successfully in the database.");

			// Track each item being added to registry for analytics
			for (const item of itemsToAdd) {
				try {
					await AnalyticsManager.trackRegistryItem('registry_item_added', {
						id: item.id || `${item.name}-${Date.now()}`, // Generate ID if not present
						name: item.name,
						category: item.category,
						subCategory: item.subCategory,
						price: item.price,
						url: item.url,
						image: item.image
					}, currentUser?.uid);
				} catch (trackingError) {
					// Don't block the main process if tracking fails
				}
			}

			setNewItems(updatedItems);
			return true;
		} catch (error) {
			console.error("Failed to add registry items:", error);
			return false;
		} finally {
			setIsAddingItems(false);
		}
	};

	const handleImageSelection = (event) => {
		const file = event.target.files[0];
		if (file && file.type.startsWith("image/")) {
			setNewItem((prevState) => ({
				...prevState,
				image: file,
			}));
		}
	};

	const renderDeleteButton = (item) => {
		if (isOwner) {
			return (
				<Pressable style={styles.deleteButton} onPress={() => deleteItem(item)}>
					<Text style={styles.deleteButtonText}>Delete</Text>
				</Pressable>
			);
		}
		return null;
	};

	const savePageConfig = async (updatedItems = null, updatedWidgets = null) => {
		if (isAdminOwnerPreview) {
			showPreviewNotice("Preview updated — changes are not saved to this account.");
			if (updatedItems !== null) setNewItems(updatedItems);
			if (updatedWidgets !== null) setWidgets(updatedWidgets);
			setHasChanges(false);
			return;
		}
		const userEmail = currentUser.email.toLowerCase();
		const userDocRef = doc(db, "users", userEmail);

		try {
			// Get the current document data first
			const docSnap = await getDoc(userDocRef);
			const existingData = docSnap.exists() ? docSnap.data() : {};
			const existingConfig = existingData.pages?.registryPageConfig || {};

			// Use updated items if provided, otherwise use existing items or current newItems state
			const itemsToSave = updatedItems !== null ? updatedItems : (existingConfig.items || newItems);
			
			// Use updated widgets if provided, otherwise use current widgets state
			const widgetsToSave = updatedWidgets !== null ? updatedWidgets : widgets;

			// Prepare the updated registry config
			const registryPageConfig = {
				...existingConfig,
				items: itemsToSave,
				widgets: widgetsToSave.map(widget => ({
					id: widget.id,
					type: widget.type,
					content: widget.content,
					state: widget.state, // Save any widget-specific state
					metadata: widget.metadata || {}
				}))
			};

			// Update the document with merged data
			await setDoc(userDocRef, {
				pages: {
					...existingData.pages,
					registryPageConfig
				}
			}, { merge: true });

			setShowErrorPopup(false);
			setSaveError("");
			setShowSavePopup(true);
			setHasChanges(false);

			Animated.timing(savePopupAnim, {
				toValue: 1,
				duration: 500,
				useNativeDriver: true,
			}).start(() => {
				setTimeout(() => {
					Animated.timing(savePopupAnim, {
						toValue: 0,
						duration: 500,
						useNativeDriver: true,
					}).start(() => setShowSavePopup(false));
				}, 2000);
			});
		} catch (error) {
			setShowSavePopup(false);
			setSaveError("Failed to save configuration. Please try again.");
			setShowErrorPopup(true);
			Animated.timing(errorPopupAnim, {
				toValue: 1,
				duration: 500,
				useNativeDriver: true,
			}).start(() => {
				setTimeout(() => {
					Animated.timing(errorPopupAnim, {
						toValue: 0,
						duration: 500,
						useNativeDriver: true,
					}).start(() => setShowErrorPopup(false));
				}, 2000);
			});
		}
	};

	const extractUIDFromURL = async () => {
		const url = window.location.href;

		const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
		const routeSegment = extractMyTasselRouteSegment(pathname, 'registry');
		const vanitySlug = extractMyTasselVanitySlug(pathname, 'registry');
		if (routeSegment || vanitySlug) {
			const resolvedUid = await resolveMyTasselRouteUid({
				db,
				segment: routeSegment,
				slug: vanitySlug,
				pageType: 'registry',
			});
			if (resolvedUid) return resolvedUid;
		}

		if (url.includes("username===")) {
			const splitUrl = url.split("username===");
			const uid = splitUrl.length === 2 ? splitUrl[1] : null;
			return uid;
		} else if (/\/registry\/[A-Za-z]/.test(url)) {
			const regex = /\/registry\/(\w+)/;
			const match = regex.exec(url);
			return match && match.length > 1 ? match[1] : null;
		} else if (url.includes("?username=")) {
			const params = new URLSearchParams(url.split("?")[1]);
			const uid = params.get("username");
			const filteredUID = uid.replace(/=/g, "");
			return filteredUID;
		} else if (url.includes("?0")) {
			const regex = /[?&]0=%3D&1=%3D&2=%3D&3=(\w+)&4=(\d+)&5=(\d+)&6=(\d+)&7=(\d+)&8=(\d+)/;
			const match = regex.exec(url);
			if (match) {
				var uid = match.slice(1).join("");
				return uid;
			} else {
				return null;
			}
		}

		return null;
	};

	const loadPageConfig = useCallback(async () => {
		try {
			const uid = await extractUIDFromURL();
			if (!uid) {
				return;
			}

			// F-prefixed UIDs are always Friends & Family accounts — no need to query Firestore
			if (/^F\d/i.test(uid)) {
				setPageNotFound(true);
				setPageNotFoundReason('wrong_account_type');
				return;
			}

			const usersRef = collection(db, "users");
			const q = query(usersRef, where("uid", "==", uid));
			const querySnapshot = await getDocs(q);

		if (querySnapshot.empty) {
			setPageNotFound(true);
			setPageNotFoundReason('not_found');
			return;
		}

		querySnapshot.forEach((doc) => {
			const userData = doc.data();
			const accountType = userData.accountType || '';
			const isGraduate = accountType === 'Graduate' || accountType === 'G' || accountType === 'admin';
			if (!isGraduate) {
				setPageNotFound(true);
				setPageNotFoundReason('wrong_account_type');
				return;
			}
			// doc.id is the owner's email (Firestore document ID); include it as
			// a fallback in case the email field was not explicitly stored.
			setPageOwner({ ...userData, email: userData.email || doc.id });

				const registryPageConfig = userData.pages?.registryPageConfig;
				if (registryPageConfig) {
					// Load items
					setNewItems(registryPageConfig.items || []);
					
					// Load widget states
					if (registryPageConfig.widgets) {
						setWidgets(registryPageConfig.widgets.map(widget => ({
							...widget,
							state: widget.state || {},
							metadata: widget.metadata || {}
						})));
					}

					setTextColor(registryPageConfig.registryTextColor || "#000000");
				} else {
					setWidgets([]);
				}
			});
		} catch (error) {
			// Silent fail for page config load
		}
	}, [db, predefinedBackgrounds]);

	useFocusEffect(
		useCallback(() => {
			loadPageConfig();
		}, [currentUser])
	);

	const loadUserData = async () => {
		if (isPageOwnerReady) {
			return;
		}

		try {
			const uid = await extractUIDFromURL();

			if (!uid) {
				return;
			}

			setPageOwnerId(uid);

			const usersRef = collection(db, "users");
			const querySnapshot = await getDocs(query(usersRef, where("uid", "==", uid)));

		if (querySnapshot.empty) {
			setPageNotFound(true);
			setPageNotFoundReason('not_found');
			setIsPageOwnerReady(true);
		} else {
			querySnapshot.forEach((doc) => {
				const userData = doc.data();
				const accountType = userData.accountType || '';
				const isGraduate = accountType === 'Graduate' || accountType === 'G' || accountType === 'admin';
				if (!isGraduate) {
					setPageNotFound(true);
					setPageNotFoundReason('wrong_account_type');
				}
				setPageOwner({ ...userData, email: userData.email || doc.id });
				setIsPageOwnerReady(true);
			});
		}
	} catch (error) {
		// Silent fail for user data fetch
	}
};

	const toggleEditMode = () => {
		setEditableMode(!editableMode);
		if (editableMode && !isAdminOwnerPreview) {
			// Save current state when exiting edit mode
			savePageConfig();
		} else if (editableMode && isAdminOwnerPreview) {
			showPreviewNotice("Preview editor closed. Changes are not saved.");
		}
	};

	const renderEditButton = () => {
		// Hide buttons when header tutorials are active
		const headerTutorials = ['friends', 'settings'];
		const shouldHideButtons = activePopupId && headerTutorials.includes(activePopupId);

		if ((isOwner || isAdminOwnerPreview) && !editableMode && !isVisitorView && !shouldHideButtons) {
			// Shared base matching Events editButton exactly
			const btnBase = {
				paddingVertical: 10,
				paddingHorizontal: 10,
				borderRadius: 20,
				shadowOffset: { width: 0, height: 2 },
				shadowOpacity: 0.25,
				shadowRadius: 3.84,
				elevation: 5,
				width: '20%',
				top: isMobile ? 100 : isTablet ? 115 : 130,
				position: 'fixed',
				zIndex: 1000,
				transition: 'all 1s ease',
			};
			// Rightmost: Browse Catalogue (cream, like Events editButton)
			const catalogueBtnStyle = Object.assign({}, btnBase, {
				right: isMobile ? 20 : isTablet ? 30 : 40,
				backgroundColor: '#fefde2',
			});
			// Second from right: View as Visitor (green, like Events viewButton)
			const visitorBtnStyle = Object.assign({}, btnBase, {
				right: isMobile ? 'calc(20% + 28px)' : isTablet ? 'calc(20% + 38px)' : isSmallDesktop ? 'calc(20% + 48px)' : 'calc(20% + 68px)',
				backgroundColor: '#4c956c',
			});

			return (
				<>
				{/* Owner Preview Banner — fixed on the left, same row as action buttons */}
				{isPreviewingAsOwner && !isMobile && (
					<View style={{
						position: 'fixed',
						top: 120,
						left: '4%',
						width: '20%',
						minWidth: 200,
						maxWidth: 300,
						borderRadius: 20,
						backgroundColor: '#fefde2',
						borderWidth: 1,
						borderColor: 'rgba(76, 149, 108, 0.35)',
						flexDirection: 'row',
						overflow: 'hidden',
						shadowColor: '#000',
						shadowOffset: { width: 0, height: 2 },
						shadowOpacity: 0.12,
						shadowRadius: 4,
						elevation: 3,
						zIndex: 100005,
					}}>
						<View style={{ width: 5, backgroundColor: '#4c956c' }} />
						<View style={{ flex: 1, paddingVertical: 10, paddingHorizontal: 12, gap: 3 }}>
							<View style={{ paddingHorizontal: 8, paddingVertical: 2, borderRadius: 999, backgroundColor: 'rgba(76, 149, 108, 0.12)', borderWidth: 1, borderColor: 'rgba(76, 149, 108, 0.25)', alignSelf: 'flex-start' }}>
								<Text style={{ fontFamily: 'Bodoni72Smallcaps', fontSize: 10, color: '#2d5a3f', letterSpacing: 0.3 }}>Owner Preview</Text>
							</View>
							<Text style={{ fontFamily: 'Orpheus Pro', fontWeight: '600', fontSize: 13, color: '#2d5a3f' }} numberOfLines={1}>
								{ownerDisplayName}'s Registry
							</Text>
							<Text style={{ fontFamily: 'Orpheus Pro', fontSize: 11, color: '#4c6b57', opacity: 0.85 }}>
								Changes are local only
							</Text>
						</View>
					</View>
				)}

				{/* View as Visitor — green, to the left of Browse Catalogue (admin preview desktop only) */}
				{isPreviewingAsOwner && !isMobile && (
					<Pressable
						onPress={() => setIsVisitorView(true)}
						style={visitorBtnStyle}
						onMouseEnter={() => setHoveredButton('viewAsVisitor')}
						onMouseLeave={() => setHoveredButton(null)}
					>
						<Text style={styles.editButtonText}>View as Visitor</Text>
					</Pressable>
				)}

				{/* Catalogue Tutorial */}
				<TutorialPopup
					id="registryCatalogue"
					position={{
						top: isMobile ? 160 : isSmallDesktop ? 160 : 160,
						right: isMobile ? 0 : isSmallDesktop ? 50 : 90
					}}
					title="Catalogue"
					openDirection="bottomLeft"
				/>

				{/* Browse Catalogue — cream, rightmost (like Events "Edit Page Settings") */}
				<Pressable
					style={catalogueBtnStyle}
					{...spotlightTarget(SPOTLIGHT_TARGETS.REGISTRY_BROWSE_CATALOGUE)}
					onPress={() => setShowCatalogueModal(true)}
					onMouseEnter={() => setHoveredButton('catalogue')}
					onMouseLeave={() => setHoveredButton(null)}
				>
					<Text style={[styles.editButtonText, { color: '#4c956c' }]}>Browse Catalogue</Text>
				</Pressable>
				</>
			);
	} else if ((isOwner || isAdminOwnerPreview) && editableMode) {
		return (
			<Pressable style={styles.editButton} onPress={toggleEditMode}>
				<Text style={styles.editButtonText}>View Page</Text>
			</Pressable>
		);
	}
};

const redirectToUrl = (item) => {
		let url = item?.url;

		if (!url) return;

		// Normalize relative or protocol-less URLs to absolute
		if (typeof window !== 'undefined') {
			if (url.startsWith('/')) {
				const base = window.location.origin || 'https://mytassel.com';
				url = base + url;
			} else if (!/^https?:\/\//i.test(url)) {
				url = `https://${url}`;
			}
		}

		setSelectedItemForPurchase({ ...item, url });
		setShowShippingAddressPopup(true);
	};





	const renderBackgroundButton = () => {
		if (isOwner && editableMode) {
			return (
				<Pressable style={styles.backgroundButton} onPress={() => setShowBackgroundModal(true)}>
					<Text style={styles.editButtonText}>Background and Colors</Text>
				</Pressable>
			);
		}
	};

	// Function to handle background image upload
	const handleBackgroundUpload = async (event) => {
		if (!myTasselPlus.canUseFeature(MYTASSEL_PLUS_FEATURES.CUSTOM_BACKGROUND)) {
			event.target.value = '';
			myTasselPlus.openUpsell(MYTASSEL_PLUS_FEATURES.CUSTOM_BACKGROUND);
			return;
		}

		if (isAdminOwnerPreview) {
			if (event.target.files && event.target.files.length > 0) {
				const previewUrl = URL.createObjectURL(event.target.files[0]);
				setBackgroundImage(previewUrl);
				showPreviewNotice("Background updated in preview. Changes are not saved.");
			}
			return;
		}
		// Check if the event has a file list and the list is not empty
		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files[0];
			const storageRef = ref(getStorage(app), `eventBackgrounds/${currentUser.uid}/${file.name}`);
			uploadBytes(storageRef, file)
				.then((snapshot) => {
					getDownloadURL(snapshot.ref).then((downloadURL) => {
						setBackgroundImage(downloadURL);
						// Optionally, save the URL to Firestore here or in applyChanges
					});
				})
				.catch((error) => {
					// Upload failed silently
				});
		}
	};

	const triggerBackgroundUpload = () => {
		myTasselPlus.requireFeature(MYTASSEL_PLUS_FEATURES.CUSTOM_BACKGROUND, () => {
			backgroundFileInputRef.current?.click();
		});
	};

	// Function to apply background and color changes
	const applyChanges = async () => {
		if (isAdminOwnerPreview) {
			setShowBackgroundModal(false);
			showPreviewNotice("Background updated in preview. Changes are not saved.");
			return;
		}
		const userDocRef = doc(db, "users", currentUser.email.toLowerCase());

		// The document data should be an object
		const pageConfig = {
			registryCustomBackground: backgroundImage,
			registryTextColor: selectedColor,
			registryBlurEffect: applyBlur,
		};

		// Use setDoc to merge the new page config into the existing 'pages' object
		try {
			await setDoc(userDocRef, { pages: { registryPageConfig: pageConfig } }, { merge: true });
			setShowBackgroundModal(false);
		} catch (error) {
			// Silent fail for applying changes
		}
	};

	// Function to toggle blur effect
	const toggleBlur = () => {
		setApplyBlur(!applyBlur);
	};

	// Blur
	const toggleModalBlur = (type = null) => {
		setIsBlurred(!isBlurred); // Toggle the blur
		setPopupType(type); // Set the popup type
	};
	//#endregion

	//#endregion

	//#region - Initialization
	// Call loadCatalogueItems when component mounts or when items are added/removed
	useEffect(() => {
		setTimeout(() => {
			loadUserData();
			loadPageConfig();
			loadCatalogueItems();
		}, 100);
	}, []);

	useEffect(() => {
		if (isPageOwnerReady && pageOwner) {
			// console.log("PageOwner updated:", pageOwner);
		} else {
			loadUserData();
			loadPageConfig();
		}
	}, [pageOwner]);

	// Inside the useEffect that checks for page ownership
	const checkOwnership = () => {
		if (!currentUserData || !pageOwnerId) {
			setIsOwner(false);
			return;
		}

		const owner = currentUserData.uid === pageOwnerId;
		setIsOwner(owner);
		// console.log("Logged In User:", currentUserData, "isPageOwner:", owner);
	};

	// Call this function within useEffect to check for ownership
	useEffect(() => {
		checkOwnership();
		// console.warn("currentUserData:", currentUserData);
	}, [currentUserData, pageOwnerId]);

	useEffect(() => {
		// console.warn("Registry Updated!", registryItems);
		loadPageConfig();
	}, [registryItems]);

	// Load session purchaser info on mount
	useEffect(() => {
		if (typeof window !== 'undefined') {
			const savedInfo = sessionStorage.getItem('purchaserInfo');
			if (savedInfo) {
				try {
					const parsedInfo = JSON.parse(savedInfo);
					setSessionPurchaserInfo(parsedInfo);
					setPurchaserInfo(parsedInfo);
				} catch (error) {
					// Silent fail for parsing purchaser info
				}
			}
		}
	}, []);

	useEffect(() => {
		const checkAuthorization = async () => {
			// Wait until both the page owner data AND the Firebase auth state have resolved.
			if (!pageOwner || authLoading) return;

			const urlParams = new URLSearchParams(window.location.search);
			const uic = urlParams.get('uic');

			if (uic && uic === pageOwner.uic) {
				setIsAuthorized(true);
				return;
			}

		// Admins can always access any page regardless of privacy settings.
		if (currentUserData?.accountType === 'admin') {
			setIsAuthorized(true);
			return;
		}

		if (pageOwner.uid === currentUserData?.uid) {
			setIsAuthorized(true);
			return;
		}

		if (!pageOwner.isFriendsListPrivate) {
			setIsAuthorized(true);
			return;
		}

		if (currentUserData) {
			if (pageOwner.friends?.followers && pageOwner.friends.followers[currentUserData.uid]) {
				setIsAuthorized(true);
				return;
			}
		}

		setIsAuthorized(false);
		};

		checkAuthorization();
	}, [pageOwner, currentUserData, authLoading]);

	if (pageNotFound) {
		return <PageUnavailableMessage reason={pageNotFoundReason} />;
	}

	// Don't show "private" until auth has fully resolved — prevents false flash for admins
	if (!isAuthorized && pageOwner && !authLoading) {
		return <PrivatePageMessage pageOwner={pageOwner} />;
	}
	//#endregion

	// #region - Page Interaction Functions
	// Function to handle showing purchase recording modal
	const handleConfirmPurchase = async (item) => {
		// Show the purchase recording modal instead of directly confirming
		setShowDidYouBuyPopup(false);
		setShowPurchaseRecordModal(true);
	};

	// Function to handle actual purchase confirmation after recording
	const handleActualPurchaseConfirmation = async (item, purchaserData = null, isAnonymous = false) => {
		if (isAdminOwnerPreview) {
			showPreviewNotice("Purchases cannot be recorded in preview mode.");
			return;
		}
		// Prevent double-clicks and concurrent purchases
		if (isProcessing) {
			return;
		}

		// Guard against missing item or page owner email before touching Firestore
		if (!item) {
			alert("Error: No item selected. Please select an item and try again.");
			return;
		}
		if (!pageOwner?.email) {
			alert("Error: Unable to locate registry. Please refresh the page and try again.");
			return;
		}

		setIsProcessing(true); // Start processing

		try {
			// Generate the formatted purchase time
			const formattedPurchaseTime = new Date().toLocaleString("en-US", {
				weekday: "long",
				year: "numeric",
				month: "long",
				day: "numeric",
				hour: "numeric",
				minute: "numeric",
				second: "numeric",
			});

			// Retrieve the current registryPageConfig from Firestore
			const userDocRef = doc(db, "users", pageOwner.email.toLowerCase());
			const docSnap = await getDoc(userDocRef);
			if (docSnap.exists()) {
				// Get fresh data from the database, not from local state
				const freshUserData = docSnap.data();
				const registryPageConfig = freshUserData.pages?.registryPageConfig;

				if (registryPageConfig && registryPageConfig.items) {
					// Find the item in registryPageConfig.items and update it
						const updatedItems = registryPageConfig.items.map((it) => {
							// Use loose equality to handle string vs number ID comparisons
							if (it.id == item.id) {
								const purchaseData = {
									...it, 
									purchased: true, 
									purchaseTime: formattedPurchaseTime,
									purchasedBy: isAnonymous ? null : purchaserData,
									isAnonymousPurchase: isAnonymous
								};
								return purchaseData;
							}
							return it;
						});

						// Check if any item was actually updated
						const wasItemUpdated = updatedItems.some(it => it.id == item.id && it.purchased);
						if (!wasItemUpdated) {
							alert("Error: Item not found in registry. Please refresh the page and try again.");
							setIsProcessing(false);
							return;
						}

					// Create updated registry config
					const updatedRegistryConfig = { ...registryPageConfig, items: updatedItems };

					// Update the registryPageConfig in Firestore
					await setDoc(
						userDocRef,
						{
							pages: {
								...freshUserData.pages, // Preserve other page configurations
								registryPageConfig: updatedRegistryConfig,
							},
						},
						{ merge: true }
					);

					// Update local states to reflect the change immediately
					setNewItems(updatedItems);
					
					// Update pageOwner state to prevent stale data issues
					setPageOwner(prevPageOwner => ({
						...prevPageOwner,
						pages: {
							...prevPageOwner.pages,
							registryPageConfig: updatedRegistryConfig
						}
					}));

					// Send purchase notification email to registry owner
					try {
						const emailPayload = {
							graduateName: `${pageOwner.firstName} ${pageOwner.lastName}`.trim(),
							graduateEmail: pageOwner.email,
							purchaserName: isAnonymous ? 'Anonymous' : (purchaserData?.name || 'Someone'),
							purchaserEmail: isAnonymous ? null : (purchaserData?.email || null),
							itemName: item.name,
							itemPrice: item.price || null,
							itemUrl: item.url || null,
							message: isAnonymous
								? "Someone purchased this item for you anonymously!"
								: `${purchaserData?.name || 'Someone'} purchased this item for you!`
						};

						const response = await fetch(`${SERVER_URL}/send-purchase-confirmation-email`, {
							method: 'POST',
							headers: {
								'Content-Type': 'application/json',
							},
							body: JSON.stringify(emailPayload),
						});

						// Email sent or failed silently - purchase is still recorded
					} catch (emailError) {
						// Continue with the process even if email fails - purchase is still recorded!
					}
				} else {
					alert("Error: Unable to find registry configuration.");
				}
			} else {
				alert("Error: User document not found.");
			}

			setIsProcessing(false);
			setShowDidYouBuyPopup(false);
			setShowPurchaseRecordModal(false);
			setShowAnonymousConfirmModal(false);
			setShowPurchaserInfoModal(false);
			setSelectedItemForPurchase(null); // Reset the selected item for purchase
			setPurchaseRecordError("");

			// Show the confirmation container
			setShowConfirmationContainer(true);
		} catch (error) {
			// Provide more specific error messages
			if (error.code === 'unavailable') {
				alert("Network error: Unable to connect to the server. Please check your internet connection and try again.");
			} else if (error.code === 'permission-denied') {
				alert("Permission error: You may need to refresh the page and try again.");
			} else {
				alert("An error occurred while confirming your purchase. Please refresh the page and try again.");
			}
			
			setIsProcessing(false);
			// Reset the popup state on error
			setShowDidYouBuyPopup(false);
			setShowPurchaseRecordModal(false);
			setShowAnonymousConfirmModal(false);
			setShowPurchaserInfoModal(false);
			setSelectedItemForPurchase(null);
			setPurchaseRecordError("");
		}
	};

	const handleShowShippingAddress = () => {
		if (pageOwner && (pageOwner.shippingAddress || pageOwner.shippingCity || pageOwner.shippingState || pageOwner.shippingZip)) {
			// Format the complete address with all components
			const street = pageOwner.shippingAddress || "";
			const city = pageOwner.shippingCity || "";
			const state = pageOwner.shippingState || "";
			const zip = pageOwner.shippingZip || "";
			
			let formattedAddress = "";
			
			// Add street address if available
			if (street) {
				formattedAddress += street;
			}
			
			// Add city, state, zip on new line if any are available
			const cityStateZip = [];
			if (city) cityStateZip.push(city);
			if (state && zip) {
				cityStateZip.push(`${state} ${zip}`);
			} else if (state) {
				cityStateZip.push(state);
			} else if (zip) {
				cityStateZip.push(zip);
			}
			
			if (cityStateZip.length > 0) {
				if (formattedAddress) formattedAddress += "\n";
				formattedAddress += cityStateZip.join(", ");
			}
			
			// Fallback to just street if that's all we have
			if (!formattedAddress && street) {
				formattedAddress = street;
			}

			setShippingAddress(formattedAddress || "Address information incomplete");
			setShowShippingAddressPopup(true);
		} else {
			alert("This user has not set up a shipping address. Please contact them directly to ask where they want their gift sent.");
		}
	};

	// Function to handle showing the "Did you buy this?" popup
	const handleDidYouBuy = (item) => {
		setShowShippingAddressPopup(false);
		setSelectedItemForPurchase(item);
		setShowDidYouBuyPopup(true);
	};

	// Function to handle "Let them know" button
	const handleLetThemKnow = async () => {
		if (currentUser && currentUserData) {
			// Authenticated user - use their data
			const purchaserData = {
				uid: currentUser.uid,
				name: `${currentUserData.firstName || ''} ${currentUserData.lastName || ''}`.trim(),
				email: currentUserData.email,
				phone: currentUserData.phone || null,
				profilePicture: currentUserData.profilePictureURL || null
			};
			setShowPurchaseRecordModal(false);
			await handleActualPurchaseConfirmation(selectedItemForPurchase, purchaserData, false);
		} else {
			// Unauthenticated user - check if we have saved info
			if (sessionPurchaserInfo && sessionPurchaserInfo.name && (sessionPurchaserInfo.email || sessionPurchaserInfo.phone)) {
				// Use saved session info directly
				setShowPurchaseRecordModal(false);
				await handleActualPurchaseConfirmation(selectedItemForPurchase, sessionPurchaserInfo, false);
			} else {
				// No saved info - show info collection modal
				setShowPurchaseRecordModal(false);
				setShowPurchaserInfoModal(true);
			}
		}
	};

	// Function to handle "Change user information" button
	const handleChangeUserInfo = () => {
		setShowPurchaseRecordModal(false);
		setShowPurchaserInfoModal(true);
	};

	// Function to handle remain anonymous click
	const handleRemainAnonymous = () => {
		setShowPurchaseRecordModal(false);
		setShowAnonymousConfirmModal(true);
	};

	// Function to confirm anonymous purchase
	const handleConfirmAnonymous = async () => {
		setShowAnonymousConfirmModal(false);
		await handleActualPurchaseConfirmation(selectedItemForPurchase, null, true);
	};

	// Function to save purchaser info and complete purchase
	const handleSavePurchaserInfo = async () => {
		// Validation
		if (!purchaserInfo.name.trim()) {
			setPurchaseRecordError("Name is required");
			return;
		}
		if (!purchaserInfo.email.trim() && !purchaserInfo.phone.trim()) {
			setPurchaseRecordError("Please provide at least one contact method (email or phone)");
			return;
		}

		// Save to session storage for future purchases
		if (typeof window !== 'undefined') {
			sessionStorage.setItem('purchaserInfo', JSON.stringify(purchaserInfo));
			setSessionPurchaserInfo(purchaserInfo);
		}

		setShowPurchaserInfoModal(false);
		await handleActualPurchaseConfirmation(selectedItemForPurchase, purchaserInfo, false);
	};

	// Function to refuse providing info (anonymous purchase)
	const handleRefuseInfo = async () => {
		setShowPurchaserInfoModal(false);
		await handleActualPurchaseConfirmation(selectedItemForPurchase, null, true);
	};

	const handleAnnouncementsPress = () => {
		if (pageOwner && pageOwner.pages.events) {
			const urlParams = new URLSearchParams(window.location.search);
			const uic = urlParams.get('uic');

			// Use environment-aware base URL instead of stored production URL
			const publicUrls = getMyTasselPublicUrls({ pageOwner, pageOwnerId, baseUrl: getBaseUrl() });
			let eventsUrl = publicUrls.events;
			if (uic) {
				eventsUrl += `?uic=${uic}`; // Append the UIC to the events URL
			}

			Linking.openURL(eventsUrl);
		}
	};

	//#endregion

	// Add a new component to render the confirmation container
	const renderConfirmationContainer = () => {
		if (!showConfirmationContainer) return null;

		return (
			<View style={styles.fixedContainer}>
				<View style={styles.modalContainer}>
					<View style={styles.modalContent}>
						<Text style={styles.modalQuestionTitle}>Purchase Confirmed!</Text>
						<Text style={styles.modalDisplayItemName}>Thank you for your purchase! The registry has been updated.</Text>
						<TouchableOpacity style={styles.denyButton} onPress={() => setShowConfirmationContainer(false)}>
							<Text style={styles.buttonText}>Close</Text>
						</TouchableOpacity>
					</View>
				</View>
			</View>
		);
	};

	// Add a function to handle widget removal
	const removeWidget = async (widgetId) => {
		try {
			const updatedWidgets = widgets.filter(w => w.id !== widgetId);
			setWidgets(updatedWidgets);
			if (!isAdminOwnerPreview) {
				// Save the updated widgets immediately
				await savePageConfig(null, updatedWidgets);
			} else {
				showPreviewNotice("Widget removed in preview. Changes are not saved.");
			}
			setHasChanges(true);
		} catch (error) {
			alert("Failed to remove widget. Please try again.");
		}
	};

	// Add a function to handle widget state updates
	const updateWidgetState = (widgetId, newState) => {
		setWidgets(currentWidgets => {
			const updatedWidgets = currentWidgets.map(widget => {
				if (widget.id === widgetId) {
					return {
						...widget,
						state: {
							...widget.state,
							...newState
						}
					};
				}
				return widget;
			});
			return updatedWidgets;
		});
		setHasChanges(true);
	};

	return (
		<>
			<SEOHead
				title={`${pageOwner?.firstName}'s Graduation Registry | MyTassel`}
				description={`View and contribute to ${pageOwner?.firstName}'s graduation registry from ${pageOwner?.organization?.name}`}
				image={pageOwner?.profilePictureURL}
				url={`https://mytassel.com/registry/${pageOwnerId}`}
				type="product"
			/>
			<ProductStructuredData 
				product={{
					name: `${pageOwner?.firstName}'s Graduation Registry`,
					description: `Graduation registry for ${pageOwner?.firstName} ${pageOwner?.lastName} from ${pageOwner?.organization?.name}`,
					// Add other product-specific structured data
				}}
			/>
			<View style={{
				...styles.body,
				width: '100vw',
				maxWidth: '100vw',
				overflowX: 'hidden',
				boxSizing: 'border-box',
			}}>
				<View style={styles.container}>
					<ImageBackground source={backgroundImage ? { uri: backgroundImage } : require("../assets/Hub Background.png")} style={styles.backgroundImage} blurRadius={applyBlur || showCatalogueModal ? 10 : 0}>
						{/* Apply blur effect based on state */}
						{applyBlur && <View style={styles.overlayBlur} />}
						{/* Header Bar */}
						<HeaderBar headerTranslateY={headerTranslateY} isModalOpen={showCatalogueModal} />

						<ScrollView style={styles.body} onScroll={onScroll} contentContainerStyle={{ paddingTop: isMobile ? HEADER_PADDING.mobile : isTablet ? HEADER_PADDING.tablet : HEADER_PADDING.desktop, flexGrow: 1 }}>

							{/* Edit Button */}
							{(isOwner || isAdminOwnerPreview) && renderEditButton()}


							{/* Customization Button */}
							{renderBackgroundButton()}

							{/* Background Customization Modal */}
							{showBackgroundModal && (
								<Modal transparent={true} animationType="slide" visible={showBackgroundModal} onRequestClose={() => setShowBackgroundModal(false)}>
									<View style={styles.modalContainer}>
										<View style={styles.modalContent}>
											<Text style={styles.modalTitle}>Customize Background and Colors</Text>

											{/* Background Selection */}
											<View style={styles.backgroundOptions}>
												<Text>Select Background:</Text>
												<ScrollView horizontal={true}>
													{predefinedBackgrounds.map((bg, index) => (
														<Pressable key={index} onPress={() => setBackgroundImage(bg)}>
															<Image style={styles.backgroundThumbnail} source={bg} />
														</Pressable>
													))}
												</ScrollView>
												<Button title={`Upload Custom Background ${!myTasselPlus.hasPlus ? "+" : ""}`} onPress={triggerBackgroundUpload} />
												<input
													type="file"
													accept="image/*"
													ref={backgroundFileInputRef}
													onChange={handleBackgroundUpload}
													style={{ display: 'none' }}
												/>
											</View>

											{/* Color Picker */}
											<View style={styles.colorPicker}>
												<Text>Select Text Color:</Text>
												<Picker selectedValue={selectedColor} style={{ height: 50, width: 150 }} onValueChange={(itemValue, itemIndex) => setSelectedColor(itemValue)}>
													<Picker.Item label="Black" value="#000000" />
													<Picker.Item label="White" value="#FFFFFF" />
													{/* Add more color options as needed */}
												</Picker>
											</View>

											<View style={styles.blurToggle}>
												<Text tyle={styles.modalSubText}>Apply Blur Effect:</Text>
												<Switch value={applyBlur} onValueChange={toggleBlur} />
											</View>

											<Button title="Apply Changes" onPress={applyChanges} />
										</View>
									</View>
								</Modal>
							)}

							{/* Item Modal */}
							{showAddItemModal && (
								<View style={{ ...styles.blurredContainer }} visible={showAddItemModal} onRequestClose={() => setShowAddItemModal(false)}>
									<View style={styles.modalContent}>
										<TextInput style={styles.input} placeholder="Item Name" onChangeText={(text) => setNewItem({ ...newItem, name: text })} value={newItem.name} />
										<TextInput style={styles.input} placeholder="Description" onChangeText={(text) => setNewItem({ ...newItem, description: text })} value={newItem.description} />
										<TextInput style={styles.input} placeholder="Price" keyboardType="numeric" onChangeText={(text) => setNewItem({ ...newItem, price: text })} value={newItem.price} />
										<TextInput style={styles.input} placeholder="URL" onChangeText={(text) => setNewItem({ ...newItem, url: text })} value={newItem.url} />
										<input type="file" accept="image/*" onChange={handleImageSelection} />
										{newItem.image && <Image source={{ uri: newItem.image }} style={styles.imagePreview} />}
										<Button title="Add" onPress={handleAddNewItem} />
										<Button title="Close" onPress={() => setShowAddItemModal(false)} />
									</View>
								</View>
							)}

						{/* Browse Catalogue: starter lists, full catalogue and success confirmation */}
						{showCatalogueModal && <RegistrySetupFlow onAddItems={addItemsToRegistry} onClose={() => setShowCatalogueModal(false)} />}

							{showItemModal && (
								<View style={{ ...styles.blurredContainer }}>
									<View style={styles.modalContent}>
										<Text style={styles.modalTitle}>{selectedItem?.name}</Text>
										<Image source={{ uri: selectedItem?.image }} style={styles.itemImage} />
										<Text style={styles.itemDescription}>{selectedItem?.description}</Text>
										<Text style={styles.itemPrice}>{`$${selectedItem?.price}`}</Text>
										<Button title="Go to URL" onPress={redirectToUrl} />
										<Button title="Close" onPress={() => setShowItemModal(false)} />
									</View>
								</View>
							)}

						{!isPageOwnerReady ? (
							<View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', paddingVertical: 80 }}>
								<ActivityIndicator size="large" color="#fefde2" />
							</View>
						) : (
						<>
						<View style={[styles.pageHeader, !isMobile && !isTablet && !((isOwner || isAdminOwnerPreview) && !isVisitorView) && { marginTop: -40, paddingTop: 0 }]}>
						{/* Mobile & Tablet Layout */}
							{(isMobile || isTablet) && (
								<View style={styles.mobileTabletLayout}>
									{/* Profile Section */}
									<View style={{ width: '100%', alignItems: 'center', marginBottom: 20 }}>
										{pageOwner ? (
											<>
												{isOwner && showTutorial && (
													<TutorialPopup
														id="registryIntro"
														position={{
															top: 150,
															left: 150,
															zIndex: 100000
														}}
														title="Registry Page"
														openDirection="bottomRight"
														style={{ zIndex: 100000 }}
													/>
												)}
												<Image style={[styles.profileImage, { width: 120, height: 120 }]} source={{ uri: pageOwner.profilePictureURL }} />
												<Text style={[styles.profileName, { textAlign: 'center', marginTop: 10 }]}>{`${pageOwner.firstName} ${pageOwner.lastName}`}</Text>
												<Text style={[styles.profileDetails, { textAlign: 'center' }]}>{pageOwner.organization.name}</Text>
												<Text style={[styles.profileDetailsSmall, { textAlign: 'center' }]}>{pageOwner?.degree}</Text>
												{pageOwner.accountType === "graduate" && <Text style={[styles.profileDetails, { textAlign: 'center' }]}>Graduation Date: {pageOwner.graduationDate}</Text>}
											</>
										) : (
											<Text>Loading profile...</Text>
										)}
									</View>

									{/* Yearbook Widget */}
									<View style={{ width: '90%', marginBottom: 20, alignSelf: 'center' }} {...spotlightTarget(SPOTLIGHT_TARGETS.REGISTRY_YEARBOOK)}>
										<Text style={styles.header}>Yearbook</Text>
										{pageOwnerId && (
											<Yearbook style={[styles.yearbookWidget, { width: '100%', maxWidth: '100%' }]} pageOwnerId={pageOwnerId} pageOwner={pageOwner} />
										)}
									</View>

									{/* Share */}
									<View style={{ width: '100%', marginBottom: 20 }}>
										{pageOwner && pageOwner.pages.registry && <Share style={[styles.shareButton, { width: '100%', maxWidth: '100%' }]} pageOwner={pageOwner} pageType={"Registry"} />}
									</View>

									{/* Graduation Fund */}
									<View style={{ width: '90%', marginBottom: 20, marginTop: isMobile ? 30 : 0, alignSelf: 'center' }}>
									{pageOwnerId && !(isOwner || isPreviewingAsOwner) && <Text style={styles.header}>{`${pageOwner?.firstName}'s Graduation Fund`}</Text>}
									{pageOwnerId && !(isOwner || isPreviewingAsOwner) && <GoalTracker pageOwnerId={pageOwnerId} pageOwner={pageOwner} isVisitorView={true} onHeightChange={handleGoalTrackerHeightChange} />}
									{pageOwnerId && (isOwner || isPreviewingAsOwner) && <Text style={styles.header}>{`My Graduation Fund`}</Text>}
									{pageOwnerId && (isOwner || isPreviewingAsOwner) && <GoalTracker pageOwnerId={pageOwnerId} pageOwner={pageOwner} isOwner={isOwner || isPreviewingAsOwner} onHeightChange={handleGoalTrackerHeightChange} />}
									</View>

									{/* Announcement Button for Mobile */}
									<View style={{ width: '100%', marginBottom: 20, alignItems: 'center' }}>
										{pageOwner && pageOwner.pages.events && (
											<Pressable 
												style={[
													styles.eventsButton,
													hoveredButton === 'events' && styles.eventsButtonHover
												]}
												onPress={handleAnnouncementsPress}
												onMouseEnter={() => setHoveredButton('events')}
												onMouseLeave={() => setHoveredButton(null)}
											>
												<Text style={styles.eventsButtonText}>
													Go to {pageOwner.firstName}'s Announcement Page
												</Text>
											</Pressable>
										)}
									</View>
								
								</View>
							)}

							{/* Responsive Desktop Layout */}
							{!isMobile && !isTablet && (
								<View style={[
									styles.desktopContainer,
								]}>
									{/* Titles Row */}
									<View style={{
										display: 'flex',
										flexDirection: 'row',
										width: '100%',
										alignItems: 'center',
										justifyContent: 'space-evenly',
									}}>
										<View style={{ 
											flex: 1, 
											justifyContent: 'center', 
											alignItems: 'center',
										}}>
											<Text style={styles.sectionTitle}>Yearbook</Text>
										</View>
										<View style={{ 
											flex: 1, 
											justifyContent: 'center', 
											alignItems: 'center' 
										}}>
											{/* Profile Title Space - Empty */}
										</View>
										<View style={{ 
											flex: 1, 
											justifyContent: 'center', 
											alignItems: 'center',
										}}>
											<Text style={styles.sectionTitle}>My Goal</Text>
										</View>
									</View>

									{/* Components Row */}
									<View style={[
										styles.componentsRow,
									]}>
										{/* Left: Yearbook */}
										<View style={[
											styles.columnContainer,
										]} {...spotlightTarget(SPOTLIGHT_TARGETS.REGISTRY_YEARBOOK)}>
										{pageOwnerId && (
											<Yearbook pageOwnerId={pageOwnerId} pageOwner={pageOwner} targetHeight={syncedHeight} onHeightChange={handleYearbookHeightChange} />
										)}
										</View>

										{/* Center: Profile */}
										<View style={[
											styles.profileColumnContainer,
										]}>
											{pageOwner ? (
												<>
													{isOwner && showTutorial && (
														<TutorialPopup
															id="registryIntro"
															position={{
																top: 150,
																left: 150,
																zIndex: 100000
															}}
															title="Registry Page"
															openDirection="bottomRight"
															style={{ zIndex: 100000 }}
														/>
													)}
													<Image style={styles.profileImage} source={{ uri: pageOwner.profilePictureURL }} />
													<Text style={styles.profileName}>{`${pageOwner.firstName} ${pageOwner.lastName}`}</Text>
													<Text style={styles.profileDetails}>{pageOwner.organization.name}</Text>
													<Text style={styles.profileDetailsSmall}>{pageOwner?.degree}</Text>
													{pageOwner.accountType === "graduate" && <Text style={styles.profileDetails}>Graduation Date: {pageOwner.graduationDate}</Text>}
												</>
											) : (
												<Text>Loading profile...</Text>
											)}
										</View>

										{/* Right: Goals */}
										<View style={[
											styles.columnContainer,
										]}>
										{pageOwnerId && !(isOwner || isPreviewingAsOwner) && (
											<GoalTracker pageOwnerId={pageOwnerId} pageOwner={pageOwner} isVisitorView={true} onHeightChange={handleGoalTrackerHeightChange} targetHeight={syncedHeight} />
										)}
										{pageOwnerId && (isOwner || isPreviewingAsOwner) && (
											<GoalTracker pageOwnerId={pageOwnerId} pageOwner={pageOwner} isOwner={isOwner || isPreviewingAsOwner} onHeightChange={handleGoalTrackerHeightChange} targetHeight={syncedHeight} />
										)}
											</View>
									</View>

									{/* Buttons Row - Perfectly Aligned */}
									<View style={{
										display: 'flex',
										flexDirection: 'row',
										width: '100%',
										alignItems: 'center',
										justifyContent: 'space-evenly',
									}}>
										{/* Share Button - Left aligned under Yearbook */}
										<View style={{ 
											flex: 1, 
											justifyContent: 'center', 
											alignItems: 'center',
										}}>
											{pageOwner && pageOwner.pages.registry && (
												<Share pageOwner={pageOwner} pageType={"Registry"} />
											)}
										</View>

										{/* Empty Center Space under Profile */}
										<View style={{ 
											flex: 1, 
											justifyContent: 'center', 
											alignItems: 'center' 
										}}>
										</View>

										{/* Announcement Button - Right aligned under Goal */}
										<View style={{ 
											flex: 1, 
											justifyContent: 'center', 
											alignItems: 'center',
										}}>
											{pageOwner && pageOwner.pages.events && (
												<Pressable 
													style={[
														styles.eventsButton,
														hoveredButton === 'events' && styles.eventsButtonHover
													]}
													onPress={handleAnnouncementsPress}
													onMouseEnter={() => setHoveredButton('events')}
													onMouseLeave={() => setHoveredButton(null)}
												>
													<Text style={styles.eventsButtonText}>
														Go to {pageOwner.firstName}'s Announcement Page
													</Text>
												</Pressable>
											)}
										</View>
									</View>
								</View>
							)}
						</View>
							{/* Content Section */}
							<View style={styles.section} {...spotlightTargetWhen(sortedItems.length > 0, SPOTLIGHT_TARGETS.REGISTRY_ITEMS_SECTION)}>
								{/* Grid Layout for Displaying Catalogue Items */}
								<View style={styles.gridContainer}>
									{sortedItems.map((item, index) => renderItem(item, index))}
								</View>
							</View>
						</>
						)}
					</ScrollView>
					</ImageBackground>

					{showPurchaseConfirmation && !(isOwner || isAdminOwnerPreview) && (
						<View style={{ ...styles.blurredContainer }} visible={showPurchaseConfirmation} onRequestClose={() => setShowPurchaseConfirmation(false)}>
							<View style={styles.modalContainer}>
								{isProcessing ? (
									<Text style={styles.buttonText}>Thank you! Processing your purchase...</Text> // Show processing message
								) : (
									<View style={{ ...styles.blurredContainer }} visible={showPurchaseConfirmation} onRequestClose={() => setShowPurchaseConfirmation(false)}>
										<View style={styles.modalContent}>
											<Text style={styles.itemName}>Confirm Purchase</Text>
											<Text style={styles.itemName}>Did you purchase this item?</Text>
											<Text style={styles.itemName}>{selectedItemForPurchase?.name}</Text>
											<View style={styles.modalButtonGroup}>
												<Pressable style={styles.continueButton} onPress={() => handleConfirmPurchase(selectedItemForPurchase)}>
													<Text style={styles.buttonText}>Yes, I did</Text>
												</Pressable>
												<Pressable style={{ ...styles.denyButton, backgroundColor: "red" }} onPress={() => setShowPurchaseConfirmation(false)}>
													<Text style={styles.buttonText}>No</Text>
												</Pressable>
											</View>
										</View>
									</View>
								)}
							</View>
						</View>
					)}

					{showShippingAddressPopup && (
						<View style={styles.fixedContainer} visible={showShippingAddressPopup} onRequestClose={() => setShowShippingAddressPopup(false)}>
							<View style={styles.modalContainer}>
								<View style={styles.modalContent}>
									<Text style={styles.modalQuestionTitle}>{pageOwner.firstName}'s Shipping Address</Text>
									<Text style={styles.modalDisplayItemName}>{shippingAddress}</Text>
									<TouchableOpacity style={styles.denyButton} onPress={() => setShowShippingAddressPopup(false)}>
										<Text style={styles.buttonText}>Close</Text>
									</TouchableOpacity>
								<TouchableOpacity 
									style={styles.continueButton} 
									onPress={() => {
										// Open in new tab FIRST, synchronously within the user gesture
										// to prevent mobile browsers from blocking the popup
										if (selectedItemForPurchase?.url) {
											const a = document.createElement('a');
											a.href = selectedItemForPurchase.url;
											a.target = '_blank';
											a.rel = 'noopener noreferrer';
											document.body.appendChild(a);
											a.click();
											document.body.removeChild(a);
										}
										setShowShippingAddressPopup(false);
										handleDidYouBuy(selectedItemForPurchase);
									}}>
									<Text style={styles.buttonText}>Continue to Site</Text>
								</TouchableOpacity>
								</View>
							</View>
						</View>
					)}

					{showDidYouBuyPopup && (
						<View style={styles.fixedContainer} visible={showDidYouBuyPopup} onRequestClose={() => setShowDidYouBuyPopup(false)}>
							<View style={styles.modalContainer}>
								<ScrollView contentContainerStyle={styles.modalContent} showsVerticalScrollIndicator={false}>
										<Text style={styles.modalQuestionTitle}>Did you purchase this item?</Text>
										<Text style={styles.modalDisplayItemName}>{selectedItemForPurchase?.name}</Text>
									<TouchableOpacity 
										style={[styles.continueButton, isProcessing && styles.disabledButton]} 
										onPress={() => !isProcessing && handleConfirmPurchase(selectedItemForPurchase)}
										disabled={isProcessing}
									>
										<Text style={styles.buttonText}>{isProcessing ? "Processing..." : "Yes"}</Text>
									</TouchableOpacity>
									<TouchableOpacity 
										style={[styles.denyButton, isProcessing && styles.disabledButton]} 
										onPress={() => !isProcessing && setShowDidYouBuyPopup(false)}
										disabled={isProcessing}
									>
										<Text style={styles.buttonText}>No</Text>
									</TouchableOpacity>
								</ScrollView>
							</View>
						</View>
					)}

					{/* Purchase Recording Modal */}
					{showPurchaseRecordModal && (
						<View style={styles.fixedContainer} visible={showPurchaseRecordModal} onRequestClose={() => setShowPurchaseRecordModal(false)}>
							<View style={styles.modalContainer}>
								<ScrollView contentContainerStyle={styles.modalContent} showsVerticalScrollIndicator={false}>
									<View style={styles.modalTextContainer}>
										<Text style={styles.modalQuestionTitle}>Let {pageOwner?.firstName} know that you purchased this item for them!</Text>
										<Text style={styles.modalDisplayItemName}>{selectedItemForPurchase?.name}</Text>
										{/* Show saved user info if available */}
										{!currentUser && sessionPurchaserInfo && sessionPurchaserInfo.name && (
											<View style={styles.savedInfoContainer}>
												<Text style={styles.savedInfoText}>Using your saved information:</Text>
												<Text style={styles.savedInfoDetail}>{sessionPurchaserInfo.name}</Text>
												{sessionPurchaserInfo.email && <Text style={styles.savedInfoDetail}>{sessionPurchaserInfo.email}</Text>}
												{sessionPurchaserInfo.phone && <Text style={styles.savedInfoDetail}>{sessionPurchaserInfo.phone}</Text>}
											</View>
										)}
									</View>
									<TouchableOpacity 
										style={styles.continueButton} 
										onPress={handleLetThemKnow}
									>
										<Text style={styles.buttonText}>
											{(!currentUser && sessionPurchaserInfo?.name) ? "Let them know it was me!" : "Let them know it was me!"}
										</Text>
									</TouchableOpacity>
									{/* Show "Change user information" button if we have saved info */}
									{!currentUser && sessionPurchaserInfo && sessionPurchaserInfo.name && (
										<TouchableOpacity 
											style={[styles.anonymousButton, { marginTop: 4 }]} 
											onPress={handleChangeUserInfo}
										>
											<Text style={styles.anonymousButtonText}>Change user information</Text>
										</TouchableOpacity>
									)}
									<TouchableOpacity 
										style={[styles.anonymousButton]} 
										onPress={handleRemainAnonymous}
									>
										<Text style={styles.anonymousButtonText}>Remain anonymous</Text>
									</TouchableOpacity>
								</ScrollView>
							</View>
						</View>
					)}

					{/* Anonymous Confirmation Modal */}
					{showAnonymousConfirmModal && (
						<View style={styles.fixedContainer} visible={showAnonymousConfirmModal} onRequestClose={() => setShowAnonymousConfirmModal(false)}>
							<View style={styles.modalContainer}>
								<ScrollView contentContainerStyle={styles.modalContent} showsVerticalScrollIndicator={false}>
									<View style={styles.modalTextContainer}>
										<Text style={styles.modalQuestionTitle}>If you remain anonymous {pageOwner?.firstName} will not know that you purchased this item for them. Are you sure you want to remain anonymous?</Text>
									</View>
									<TouchableOpacity 
										style={styles.continueButton} 
										onPress={handleConfirmAnonymous}
									>
										<Text style={styles.buttonText}>Yes, remain anonymous</Text>
									</TouchableOpacity>
									<TouchableOpacity 
										style={styles.denyButton} 
										onPress={() => {
											setShowAnonymousConfirmModal(false);
											setShowPurchaseRecordModal(true);
										}}
									>
										<Text style={styles.buttonText}>No, let them know</Text>
									</TouchableOpacity>
								</ScrollView>
							</View>
						</View>
					)}

					{/* Purchaser Info Modal for Unauthenticated Users */}
					{showPurchaserInfoModal && (
						<View style={styles.fixedContainer} visible={showPurchaserInfoModal} onRequestClose={() => setShowPurchaserInfoModal(false)}>
							<View style={styles.modalContainer}>
								<ScrollView contentContainerStyle={styles.modalContent} showsVerticalScrollIndicator={false}>
									<View style={styles.modalTextContainer}>
										<Text style={styles.modalQuestionTitle}>Let {pageOwner?.firstName} know who bought this for them!</Text>
										{purchaseRecordError ? (
											<Text style={styles.errorText}>{purchaseRecordError}</Text>
										) : null}
									</View>
									<TextInput
										style={styles.purchaseModalInput}
										placeholder="Name (required)"
										value={purchaserInfo.name}
										onChangeText={(text) => {
											setPurchaserInfo(prev => ({ ...prev, name: text }));
											setPurchaseRecordError("");
										}}
									/>
									<TextInput
										style={styles.purchaseModalInput}
										placeholder="Email (optional)"
										value={purchaserInfo.email}
										keyboardType="email-address"
										onChangeText={(text) => {
											setPurchaserInfo(prev => ({ ...prev, email: text }));
											setPurchaseRecordError("");
										}}
									/>
									<TextInput
										style={styles.purchaseModalInput}
										placeholder="Phone (optional)"
										value={purchaserInfo.phone}
										keyboardType="phone-pad"
										onChangeText={(text) => {
											setPurchaserInfo(prev => ({ ...prev, phone: text }));
											setPurchaseRecordError("");
										}}
									/>
									<Text style={styles.modalSupportText}>Please provide at least one contact method (email or phone)</Text>
									<TouchableOpacity 
										style={styles.continueButton} 
										onPress={handleSavePurchaserInfo}
									>
										<Text style={styles.buttonText}>Save & Let Them Know</Text>
									</TouchableOpacity>
									<TouchableOpacity 
										style={[styles.anonymousButton]} 
										onPress={handleRefuseInfo}
									>
										<Text style={styles.anonymousButtonText}>I don't want to provide my info</Text>
									</TouchableOpacity>
								</ScrollView>
							</View>
						</View>
					)}

					{/* Save success popup */}
					{showSavePopup && (
						<Animated.View
							style={[
								styles.savePopup,
								{ opacity: savePopupAnim }, // Bind opacity to animated value
							]}>
							<Text style={styles.savePopupText}>Profile Updated Successfully!</Text>
						</Animated.View>
					)}

					{/* Save error popup */}
					{showErrorPopup && (
						<Animated.View
							style={[
								styles.errorPopup,
								{ opacity: errorPopupAnim }, // Bind opacity to animated value
							]}>
							<Text style={styles.errorPopupText}>{saveError}</Text>
						</Animated.View>
					)}

					{/* Render the confirmation container */}
					{renderConfirmationContainer()}

					{/* Visitor view sticky banner */}
					{isVisitorView && <VisitorViewBanner onReturn={() => setIsVisitorView(false)} />}

					{/* Preview action notice toast */}
					{!!previewNotice && (
						<View style={{ position: 'fixed', bottom: isVisitorView ? 60 : 20, left: 0, right: 0, alignItems: 'center', zIndex: 10001, pointerEvents: 'none' }}>
							<View style={{ backgroundColor: 'rgba(44,90,63,0.92)', paddingVertical: 10, paddingHorizontal: 18, borderRadius: 20 }}>
								<Text style={{ fontFamily: 'Orpheus Pro', color: '#fefde2', fontSize: 13 }}>{previewNotice}</Text>
							</View>
						</View>
					)}
					{myTasselPlus.upsellModal}
				</View>
			</View>
		</>
	);
};

export default Registry;
